// Bulk grant/revoke surface against fga-api's access store (ADR-0047).
//
// The page built on this module is self-scoping: object suggestions come from
// `POST /api/list-objects` with relation `admin`, so the caller only ever sees
// targets they hold admin on, and the server remains the enforcer.
import { useQuery } from "@tanstack/react-query";
import { authorizedFetch } from "~/auth/authorizedFetch";
import { readTuples } from "./client";
import { throwIfNotOk } from "./errorEnvelope";
import type { ReadTuplesResponse } from "./fga-types";

const API_BASE = import.meta.env.VITE_FGA_API_URL || "/fga-api/api";

export type AdminObjectType = "organization" | "company" | "platform";
export type AccessStoreRole = "admin" | "writer" | "drafter" | "reader";

export const ACCESS_ROLE_ORDER: AccessStoreRole[] = [
  "admin",
  "writer",
  "drafter",
  "reader",
];

// One principal with access to an object, as `GET /api/access` reports it.
// Group subjects carry the userset form (`group:<id>#member`), which is exactly
// the subject string a revoke item needs — it round-trips unchanged.
export interface AccessPrincipal {
  subject: string;
  subjectType: "user" | "client" | "group";
  access: "direct" | "indirect";
  via: string | null;
  info: {
    id: string;
    username: string | null;
    displayName: string | null;
    email: string | null;
  } | null;
}

// The four fixed relation buckets of `GET /api/access`.
export type ObjectAccessResponse = Partial<
  Record<AccessStoreRole, AccessPrincipal[]>
>;

export interface BulkRoleIntent {
  user: string;
  relation: string;
  object: string;
}

export interface BulkSupersededGrant {
  object: string;
  role: string;
}

export interface BulkRoleItemResult {
  index: number;
  user: string;
  relation: string;
  object: string;
  outcome: "granted" | "revoked" | "unchanged" | "superseded" | "rejected";
  code?: string | null;
  message?: string | null;
  details?: Record<string, string> | null;
  superseded?: BulkSupersededGrant[] | null;
}

export interface BulkRolesResponse {
  status: "completed" | "previewed";
  counts: {
    total: number;
    granted: number;
    revoked: number;
    unchanged: number;
    superseded: number;
    rejected: number;
  };
  items: BulkRoleItemResult[];
}

// Objects of one type the caller holds admin on, e.g. every company they
// administer. `subject` is the caller's own `user:<uuid>`.
export async function listAdminObjects(
  subject: string,
  type: AdminObjectType,
): Promise<string[]> {
  const res = await authorizedFetch(`${API_BASE}/list-objects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, relation: "admin", user: subject }),
  });
  await throwIfNotOk(res);
  const data = (await res.json()) as { objects?: string[] };
  return data.objects ?? [];
}

export async function getObjectAccess(
  objectId: string,
): Promise<ObjectAccessResponse> {
  const res = await authorizedFetch(
    `${API_BASE}/access?object=${encodeURIComponent(objectId)}`,
  );
  await throwIfNotOk(res);
  return res.json();
}

async function runBulkRoles(
  method: "POST" | "DELETE",
  items: BulkRoleIntent[],
  preview: boolean,
): Promise<BulkRolesResponse> {
  const res = await authorizedFetch(
    `${API_BASE}/roles/bulk${preview ? "?preview=true" : ""}`,
    {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    },
  );
  await throwIfNotOk(res);
  return res.json();
}

export function bulkGrantRoles(
  items: BulkRoleIntent[],
  preview = false,
): Promise<BulkRolesResponse> {
  return runBulkRoles("POST", items, preview);
}

export function bulkRevokeRoles(
  items: BulkRoleIntent[],
  preview = false,
): Promise<BulkRolesResponse> {
  return runBulkRoles("DELETE", items, preview);
}

// There is no group-listing endpoint on fga-api, so groups are discovered from
// their membership tuples: a type-only object filter ("group") reads every
// tuple whose object is a group, and the distinct objects are the groups.
// A group with no members does not appear — granting to it would affect
// nobody, so it is not offered.
export async function listGroups(): Promise<string[]> {
  const groups = new Set<string>();
  let token: string | undefined;
  do {
    const data: ReadTuplesResponse = await readTuples({
      tupleKey: { object: "group" },
      continuationToken: token,
      pageSize: 100,
    });
    for (const tuple of data.tuples ?? []) groups.add(tuple.key.object);
    token = data.continuationToken || undefined;
  } while (token);
  return [...groups].sort();
}

export function useAdminObjects(type: AdminObjectType, subject: string | null) {
  return useQuery({
    queryKey: ["grants", "admin-objects", type],
    enabled: !!subject,
    queryFn: () => listAdminObjects(subject as string, type),
  });
}

export function useObjectAccess(objectId: string | null) {
  return useQuery({
    queryKey: ["grants", "access", objectId],
    enabled: !!objectId,
    queryFn: () => getObjectAccess(objectId as string),
  });
}

export function useGroups() {
  return useQuery({
    queryKey: ["grants", "groups"],
    queryFn: listGroups,
  });
}
